import React, {
    useContext, useMemo 
} from 'react';
import {
    Box, Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    FormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    hideProgress?: boolean,
    position: number,
    sectionKey: string,
    title?: string,
    subtitle?: string,
    body?: string,
    citation?: string,
    backJump?: number,
    forwardJump?: number
};

export const CircleTakeover = ({
    hideProgress = false,
    position,
    sectionKey,
    title,
    subtitle,
    body: initialBody,
    citation,
    backJump,
    forwardJump
}: Props): JSX.Element => {
    const useStyles = makeStyles(() => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            width: '100%',
            height: '100%',
            flexDirection: 'column',
            alignItems: 'center',
        },
        textSection: {
            display: 'flex',
            flexDirection: 'column',
        },
        title: {
            fontWeight: TypographyWeights.BOLD,
            zIndex: 1
        },
        subtitle: {
            fontWeight: TypographyWeights.BOLD,
            zIndex: 1,
            marginTop: '8px'
        },
        body: {
            zIndex: 1,
            marginTop: '24px'
        },
        citation: {
            zIndex: 1,
            marginTop: 'auto'
        },
        circleTakeover: {
            display: 'grid',
            gridTemplateColumns: '39px 1fr 39px',
            gridGap: '16px',
            flexDirection: 'row',
            width: '495px',
            height: '495px',
            transform: 'translate(-800px, 0)',
            borderRadius: '50%',
            padding: '80px 180px',
            animation: '$slideRight 0.5s forwards 0.2s'
        },
        slideLeft: {
            display: 'flex',
            flexDirection: 'column',
            height: '450px',
            transform: 'translate(800px, -470px)',
            animation: '$slideLeft 0.5s cubic-bezier(0, 0, 0.2, 1) forwards 0.2s'
        },
        '@keyframes slideRight': {
            '0%': {
                transform: 'translate(-800px, 0)' 
            },
            '100%': {
                transform: 'translate(-234px, 0)' 
            },
        },
        '@keyframes slideLeft': {
            '0%': {
                transform: 'translate(800px, -470px)' 
            },
            '100%': {
                transform: 'translate(0, -470px)' 
            },
        }
    }));
    const formContext = useContext(FormContext);
    const classes = useStyles();

    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);
    const body = useMemo(() => {
        if (section?.key === 'challenge') {
            return `Complete this 3-minute quiz to fully customize your ${section?.challengeName} Challenge experience.`;
        }
        return initialBody;
    }, [initialBody, section?.key, section?.challengeName]);

    const onClick = () => {
        formContext.goNext(forwardJump);
    };

    return (
        <CardContainer
            sectionKey={sectionKey}
            backJump={backJump}
            goBack={formContext.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                {!hideProgress ? (
                    <ProgressBar
                        currentPosition={position}
                        sectionKey={sectionKey}
                    />
                ) : (
                    <Box
                        style={{
                            marginBottom: '100px' 
                        }}
                    />
                )}
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Box
                            className={classes.circleTakeover}
                            style={{
                                backgroundColor: section?.colors.circleTakeover 
                            }}
                        />
                        <Box className={classes.slideLeft}>
                            <Box className={classes.textSection}>
                                <Typography
                                    variant={TypographyVariants.H2}
                                    className={classes.title}
                                    dangerouslySetInnerHTML={{
                                        __html: title 
                                    }}
                                />
                                {subtitle && (
                                    <Typography
                                        variant={TypographyVariants.H2}
                                        className={classes.subtitle}
                                        dangerouslySetInnerHTML={{
                                            __html: subtitle 
                                        }}
                                    />
                                )}
                                <Typography
                                    variant={TypographyVariants.H5}
                                    className={classes.body}
                                    dangerouslySetInnerHTML={{
                                        __html: body 
                                    }}
                                />
                            </Box>
                            <Typography
                                variant={TypographyVariants.VERY_SMALL}
                                className={classes.citation}
                                dangerouslySetInnerHTML={{
                                    __html: citation 
                                }}
                            />
                        </Box>
                        <Box
                            style={{
                                height: '500px' 
                            }}
                        />
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default CircleTakeover;
